import { TariffCard } from '@/components/global/TariffCard';
import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Box, Dialog, Grid, IconButton } from '@mui/material';
import { CloseIcon } from '@/icons';
import { getPaymentLink, getSubscriptionPlans, SubscriptionPlanDto, SubscriptionType } from '@tailored-pdf/api';
import { useCallback, useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Header, SubHeader } from './styles';
import { ROUTES } from '@/constants/constants';
import { logEvent } from '@/helpers/eventLogger';

function PaymentModal() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const {
    state: { uploadedFileId, subscriptionPlans, userInfo },
    dispatch,
  } = useContext(Context);

  const { notifyError } = useNotifications();

  const loadPlans = useCallback(async () => {
    try {
      const resp = await getSubscriptionPlans();
      dispatch({
        type: AppStateActions.SET_SUBSCRIPTION_PLANS,
        payload: { subscriptionPlans: resp.data },
      });
    } catch (e) {
      notifyError(getResponseError(e));
    }
  }, [dispatch, notifyError]);

  const handleClose = useCallback(
    () => dispatch({ type: AppStateActions.SET_NEED_PAYMENT, payload: { needPayment: false } }),
    [dispatch],
  );

  const handleChangeSubscription = useCallback(
    async (plan: SubscriptionPlanDto) => {
      try {
        const resp = await getPaymentLink({ subscriptionType: plan.subscriptionType });
        const callbackUrl = `${location.origin}${ROUTES.subscribedDodo(
          i18n.language,
          plan.subscriptionType,
          uploadedFileId ?? 'null',
        )}`;
        logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_LINK_FOLLOWED' });
        window.open(resp.data + encodeURIComponent(callbackUrl), '_self');
      } catch (e) {
        notifyError(getResponseError(e));
      }
    },
    [i18n.language, notifyError, uploadedFileId],
  );

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const trialPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.TRIAL);
  const monthlyPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.MONTHLY);
  const yearlyPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.YEARLY);

  return (
    <Dialog
      open
      maxWidth={'lg'}
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Only the close button may dismiss the modal — ignore backdrop clicks
        // (escape is disabled) to avoid accidental drop-off and hurting conversion.
        if (reason === 'backdropClick') return;
        handleClose();
      }}
    >
      <Box display={'flex'} flexDirection={'column'} padding={8} flex={'1 1 auto'}>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <Header>
          <Trans
            t={t}
            i18nKey="title"
            components={{
              accent: <Box color={'primary.main'} display={'inline-flex'} />,
            }}
          />
        </Header>
        <SubHeader>{t('subtitle')}</SubHeader>

        <Grid container spacing={8}>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={trialPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={monthlyPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={yearlyPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}

export { PaymentModal };
